import { Link } from 'react-router-dom';
import type { FC } from 'react';

interface ArticleCard {
  slug: string;
  title: string;
  excerpt: string;
  date: string;
  readTime: string;
  tags: string[];
  image: string;
}

const featuredArticles: ArticleCard[] = [
  {
    slug: 'getting-started-guide',
    title: '17c.com 注册登录完整教程：从零开始快速上手',
    excerpt: '本教程将手把手教您如何在 17c.com 完成账号注册、邮箱验证、资料设置以及安全登录的全部流程，帮助您在三分钟内顺利进入平台开始使用。',
    date: '2026-07-28',
    readTime: '5 分钟',
    tags: ['注册教程', '登录指南', '新手指南'],
    image: 'https://readdy.ai/api/search-image?query=Clean%20modern%20digital%20platform%20registration%20interface%20with%20warm%20lighting%2C%20minimalist%20technology%20design%2C%20soft%20neutral%20background%2C%20subtle%20geometric%20accents%2C%20professional%20editorial%20photography%20style%20with%20crisp%20details&width=600&height=340&seq=article-guide-01&orientation=landscape',
  },
  {
    slug: 'platform-overview',
    title: '17c.com 平台全面解析：核心功能与服务一览',
    excerpt: '全面了解 17c.com 平台提供的各项核心功能模块，包括资源管理、数据分析、协作工具和个性化服务，掌握平台完整能力。',
    date: '2026-07-25',
    readTime: '8 分钟',
    tags: ['平台介绍', '功能概览', '核心服务'],
    image: 'https://readdy.ai/api/search-image?query=Modern%20platform%20dashboard%20interface%20with%20data%20analytics%20and%20charts%2C%20warm%20minimalist%20design%2C%20soft%20cream%20background%2C%20clean%20UI%20elements%2C%20professional%20editorial%20style%20with%20elegant%20composition&width=600&height=340&seq=article-overview-02&orientation=landscape',
  },
  {
    slug: 'advanced-features-guide',
    title: '17c.com 高级功能使用指南：解锁平台全部潜力',
    excerpt: '深入探索 17c.com 的高级功能特性，包括自动化工作流、API 接口对接、高级搜索技巧和批量操作等进阶使用方法。',
    date: '2026-07-20',
    readTime: '10 分钟',
    tags: ['高级功能', 'API接口', '进阶技巧'],
    image: 'https://readdy.ai/api/search-image?query=Advanced%20technology%20workflow%20automation%20visualization%2C%20abstract%20geometric%20nodes%20and%20connections%2C%20warm%20orange%20and%20cream%20tones%2C%20minimalist%20clean%20design%2C%20professional%20editorial%20photography&width=600&height=340&seq=article-advanced-03&orientation=landscape',
  },
  {
    slug: 'resource-download-guide',
    title: '17c.com 资源中心使用指南：发现与下载优质资源',
    excerpt: '详细讲解 17c.com 资源中心的使用方法，包括资源分类浏览、搜索筛选、下载管理和资源上传分享等完整功能操作指南。',
    date: '2026-07-15',
    readTime: '7 分钟',
    tags: ['资源下载', '资源管理', '上传分享'],
    image: 'https://readdy.ai/api/search-image?query=Digital%20resource%20library%20organized%20grid%20of%20files%20and%20folders%2C%20warm%20minimalist%20interface%2C%20soft%20neutral%20background%2C%20clean%20categorization%20layout%2C%20professional%20editorial%20style%20with%20subtle%20highlights&width=600&height=340&seq=article-resource-04&orientation=landscape',
  },
];

const ArticleSection: FC = () => {
  return (
    <section className="py-16 md:py-20 bg-background-50">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-end justify-between mb-10 md:mb-14">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              热门文章推荐
            </h2>
            <p className="mt-2 text-sm text-foreground-600">
              精选平台使用教程和功能介绍，助您快速上手
            </p>
          </div>
          <Link
            to="/category/user-guide"
            className="hidden sm:inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
          >
            查看全部文章
            <i className="ri-arrow-right-line" />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
          {featuredArticles.map((article) => (
            <Link
              key={article.slug}
              to={`/article/${article.slug}`}
              className="group flex flex-col rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer overflow-hidden"
            >
              <div className="relative w-full h-44 overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="flex-1 p-4 md:p-5 flex flex-col">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-xs text-foreground-500">{article.date}</span>
                  <span className="text-xs text-accent-600 bg-accent-50 px-2 py-0.5 rounded-full font-medium">
                    {article.readTime}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                  {article.title}
                </h3>
                <p className="mt-2 text-xs text-foreground-600 leading-relaxed line-clamp-2">
                  {article.excerpt}
                </p>
                <div className="mt-auto pt-3 flex flex-wrap gap-1.5">
                  {article.tags.slice(0, 2).map((tag) => (
                    <span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2 py-0.5 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="mt-8 text-center sm:hidden">
          <Link
            to="/category/user-guide"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors cursor-pointer"
          >
            查看全部文章
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ArticleSection;